import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  MenuItem,
  InputAdornment,
  IconButton,
} from "@mui/material";
import {
  Person,
  Email,
  LocationOn,
  Phone,
  AssignmentInd,
  Visibility,
  VisibilityOff,
} from "@mui/icons-material"; // Icons

interface AddModelDialogProps {
  open: boolean;
  onClose: () => void;
  onModelAdded: () => void;
}

const AddModelDialog: React.FC<AddModelDialogProps> = ({
  open,
  onClose,
  onModelAdded,
}) => {
  const [modelName, setModelName] = useState("");
  const [year, setYear] = useState(new Date().toISOString().slice(0, 10));
  const [discontinuedYear, setDiscontinuedYear] = useState("");
  const [description, setDescription] = useState("");
  const [oemName, setOemName] = useState("");
  const [type, setType] = useState("");
  const [feature, setFeature] = useState("");

  const handleSubmit = () => {
    const newModel = {
      modelName,
      year: new Date(year).toISOString(),
      discontinued_year: discontinuedYear
        ? new Date(discontinuedYear).toISOString()
        : "",
      description,
      oem_name: oemName,
      type,
      feature,
    };

    const token = localStorage.getItem("token");
    if (!token) return;

    fetch(
      `${process.env.REACT_APP_API_BASE_URL}/vehicleModel/saveVehicleModel`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "x-api-key": token,
        },
        body: JSON.stringify(newModel),
      }
    )
      .then((res) => {
        if (!res.ok) throw new Error("Failed to add model");
        return res.json();
      })
      .then(() => {
        onModelAdded();
        onClose();
      })
      .catch((error) => console.error("Error adding model:", error));
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          backgroundColor: "#121212",
          border: "2px solid green",
          borderRadius: "12px",
          padding: "16px",
          width: "400px",
        },
      }}
    >
      <DialogTitle sx={{ color: "green", textAlign: "center" }}>
        Add New Vehicle Model
      </DialogTitle>
      <DialogContent>
        {/* Model Name */}
        <TextField
          autoFocus
          margin="dense"
          label="Model Name"
          type="text"
          fullWidth
          variant="outlined"
          value={modelName}
          onChange={(e) => setModelName(e.target.value)}
          sx={{
            input: { color: "white" },
            label: { color: "white" },
            "& .MuiOutlinedInput-root": {
              "& fieldset": { borderColor: "white" },
              "&:hover fieldset": { borderColor: "limegreen" },
              "&.Mui-focused fieldset": { borderColor: "limegreen" },
            },
          }}
        />

        {/* Year */}
        <TextField
          margin="dense"
          label="Year"
          type="date"
          fullWidth
          variant="outlined"
          value={year}
          onChange={(e) => setYear(e.target.value)}
          InputLabelProps={{
            shrink: true,
          }}
          sx={{
            input: { color: "white" },
            label: { color: "white" },
            "& .MuiOutlinedInput-root": {
              "& fieldset": { borderColor: "white" },
              "&:hover fieldset": { borderColor: "limegreen" },
              "&.Mui-focused fieldset": { borderColor: "limegreen" },
            },
          }}
        />

        {/* Discontinued Year */}
        <TextField
          margin="dense"
          label="Discontinued Year (Optional)"
          type="date"
          fullWidth
          variant="outlined"
          value={discontinuedYear}
          onChange={(e) => setDiscontinuedYear(e.target.value)}
          InputLabelProps={{
            shrink: true,
          }}
          sx={{
            input: { color: "white" },
            label: { color: "white" },
            "& .MuiOutlinedInput-root": {
              "& fieldset": { borderColor: "white" },
              "&:hover fieldset": { borderColor: "limegreen" },
              "&.Mui-focused fieldset": { borderColor: "limegreen" },
            },
          }}
        />

        {/* Description */}
        <TextField
          margin="dense"
          label="Description"
          type="text"
          fullWidth
          variant="outlined"
          multiline
          rows={3}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          sx={{
            input: { color: "white" },
            label: { color: "white" },
            "& .MuiOutlinedInput-root": {
              "& fieldset": { borderColor: "white" },
              "&:hover fieldset": { borderColor: "limegreen" },
              "&.Mui-focused fieldset": { borderColor: "limegreen" },
            },
          }}
        />

        {/* OEM Name */}
        <TextField
          margin="dense"
          label="OEM Name"
          type="text"
          fullWidth
          variant="outlined"
          value={oemName}
          onChange={(e) => setOemName(e.target.value)}
          sx={{
            input: { color: "white" },
            label: { color: "white" },
            "& .MuiOutlinedInput-root": {
              "& fieldset": { borderColor: "white" },
              "&:hover fieldset": { borderColor: "limegreen" },
              "&.Mui-focused fieldset": { borderColor: "limegreen" },
            },
          }}
        />

        {/* Type */}
        <TextField
          margin="dense"
          label="Type"
          type="text"
          fullWidth
          variant="outlined"
          value={type}
          onChange={(e) => setType(e.target.value)}
          sx={{
            input: { color: "white" },
            label: { color: "white" },
            "& .MuiOutlinedInput-root": {
              "& fieldset": { borderColor: "white" },
              "&:hover fieldset": { borderColor: "limegreen" },
              "&.Mui-focused fieldset": { borderColor: "limegreen" },
            },
          }}
        />

        {/* Feature */}
        <TextField
          margin="dense"
          label="Feature"
          type="text"
          fullWidth
          variant="outlined"
          value={feature}
          onChange={(e) => setFeature(e.target.value)}
          sx={{
            input: { color: "white" },
            label: { color: "white" },
            "& .MuiOutlinedInput-root": {
              "& fieldset": { borderColor: "white" },
              "&:hover fieldset": { borderColor: "limegreen" },
              "&.Mui-focused fieldset": { borderColor: "limegreen" },
            },
          }}
        />
      </DialogContent>
      <DialogActions sx={{ justifyContent: "center" }}>
        <Button
          onClick={onClose}
          sx={{ color: "white", border: "1px solid green" }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          sx={{ backgroundColor: "green", color: "black" }}
        >
          Add Model
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddModelDialog;
